import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button, useTheme, alpha, Paper,
  Container, Chip, Avatar, Divider, TextField, MenuItem, Select, FormControl,
  InputLabel, OutlinedInput, FormHelperText, IconButton,
  Snackbar, Alert, CircularProgress, Fade, Grow
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Work as WorkIcon,
  Verified as VerifiedIcon,
  Email as EmailIcon,
  Article as ArticleIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

import { useAuth } from '../context/AuthContext';
import { getMyProfile, updateMyProfile, User } from '../services/user';
import { uploadProfilePhoto } from '../services/upload';

const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

const SectionHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 60,
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.palette.primary.main,
  }
}));

const InfoCard = styled(MotionCard)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[8],
  }
}));

// Define available segments for dropdown
const segments = [
  'Government',
  'Investment Institution',
  'Private Sector Company',
  'RDI',
  'University',
  'Startup',
  'Solo-entrepreneur'
];

// Define available sectors for multi-select
const availableSectors = [
  'Food Processing',
  'Life Sciences & Pharma',
  'Biotechnology',
  'Automotive',
  'Renewables',
  'Machinery & Equipment',
  'Medical Devices & Technology',
  'Minerals & Metals',
  'Chemicals',
  'Building Materials',
  'Aerospace',
  'Maritime',
  'Military',
  'Robotics',
  'Petroleum',
  'Petrochemicals',
  'Mining',
  'Metal Goods',
  'Packaging',
  'Dairy',
  'Dates',
  'Retail & Wholesale',
  'HoReCa',
  'IT, Data & AI',
  'Finance & Banking',
  'Supply Chain & Logistics',
  'Construction & Development',
  'Healthcare Sector',
  'Tourism Sector',
  'Professional Education',
  'Creative Industries',
  'Hospitality Sector',
  'Sport & Lifestyle',
  'Media & Entertainment',
  'R&D & Innovation'
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user: authUser } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [profileData, setProfileData] = useState<User | null>(null);
  const [originalData, setOriginalData] = useState<User | null>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // State for validation errors
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    title?: string;
    organizationName?: string;
    segment?: string;
    country?: string;
    selectedSectors?: string;
  }>({});

  // Fetch user profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const profile = await getMyProfile();
        setProfileData(profile);
        setOriginalData(profile);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load profile',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    if (authUser) {
      fetchProfile();
    }
  }, [authUser]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;

    // Handle expertProfile nested fields
    if (name === 'expertTag' || name === 'expertOffers') {
      setProfileData(prev => {
        if (!prev) return null;
        return {
          ...prev,
          expertProfile: {
            ...prev.expertProfile,
            [name === 'expertTag' ? 'tag' : 'offers']: value
          }
        };
      });
    } else {
      setProfileData(prev => prev ? { ...prev, [name]: value } : null);
    }

    // Clear error for this field if it exists
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name as keyof typeof errors]: '' }));
    }
  };

  const handleSectorChange = (event: { target: { value: any } }) => {
    const { value } = event.target;
    setProfileData(prev => prev ? {
      ...prev,
      selectedSectors: typeof value === 'string' ? value.split(',') : value
    } : null);

    // Clear error for sectors if it exists
    if (errors.selectedSectors) {
      setErrors(prev => ({ ...prev, selectedSectors: '' }));
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];
      setSelectedImage(file);
      // Create a preview URL for the selected image
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];
      setSelectedLogo(file);
      // Create a preview URL for the selected logo
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    let valid = true;
    const newErrors: typeof errors = {};

    if (!profileData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
      valid = false;
    }

    if (!profileData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
      valid = false;
    }

    if (!profileData?.title?.trim()) {
      newErrors.title = 'Title is required';
      valid = false;
    }

    if (!profileData?.organizationName?.trim()) {
      newErrors.organizationName = 'Organization is required';
      valid = false;
    }

    if (!profileData?.segment) {
      newErrors.segment = 'Segment is required';
      valid = false;
    }

    if (!profileData?.country?.trim()) {
      newErrors.country = 'Country is required';
      valid = false;
    }

    if (!profileData?.selectedSectors || profileData.selectedSectors.length === 0) {
      newErrors.selectedSectors = 'Select at least one sector';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset to original data
      setProfileData(originalData);
      setSelectedImage(null);
      setImagePreview('');
      setSelectedLogo(null);
      setLogoPreview('');
      setErrors({});
    }
    setIsEditing(!isEditing);
  };

  const handleSubmit = async () => {
    if (!validateForm() || !profileData) return;

    try {
      setSaving(true);

      // Upload photo to Cloudinary if a new file was selected
      let uploadedPhotoUrl = profileData.photoUrl;
      if (selectedImage) {
        try {
          const uploadResult = await uploadProfilePhoto(selectedImage);
          uploadedPhotoUrl = uploadResult.url;
        } catch (error) {
          console.error('Error uploading photo:', error);
          setSnackbar({
            open: true,
            message: 'Failed to upload photo. Please try again.',
            severity: 'error'
          });
          setSaving(false);
          return;
        }
      }

      // Upload company logo to Cloudinary if a new file was selected
      let uploadedLogoUrl = profileData.companyLogoUrl;
      if (selectedLogo) {
        try {
          const uploadResult = await uploadProfilePhoto(selectedLogo);
          uploadedLogoUrl = uploadResult.url;
        } catch (error) {
          console.error('Error uploading logo:', error);
          setSnackbar({
            open: true,
            message: 'Failed to upload company logo. Please try again.',
            severity: 'error'
          });
          setSaving(false);
          return;
        }
      }

      // Prepare update payload
      const updates: Partial<User> = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        title: profileData.title,
        organizationName: profileData.organizationName,
        organizationBrief: profileData.organizationBrief,
        segment: profileData.segment,
        country: profileData.country,
        selectedSectors: profileData.selectedSectors,
        experience: profileData.experience,
      };

      // If expert, include expertProfile
      if (profileData.userType === 'expert' && profileData.expertProfile) {
        updates.expertProfile = profileData.expertProfile;
      }

      // If image was changed, include the uploaded URL
      if (selectedImage && uploadedPhotoUrl) {
        updates.photoUrl = uploadedPhotoUrl;
      }

      // If logo was changed, include the uploaded URL
      if (selectedLogo && uploadedLogoUrl) {
        updates.companyLogoUrl = uploadedLogoUrl;
      }

      // Update profile
      const updatedProfile = await updateMyProfile(updates);

      setProfileData(updatedProfile);
      setOriginalData(updatedProfile);
      setIsEditing(false);
      setSelectedImage(null);
      setImagePreview('');
      setSelectedLogo(null);
      setLogoPreview('');

      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.error?.message || 'Failed to update profile',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (!profileData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 3 }}>Failed to load profile data</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ pt: 4, pb: 6 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBackClick}
        sx={{
          mb: 3,
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'translateX(-4px)'
          }
        }}
      >
        Back
      </Button>

      {/* Hero Header */}
      <Fade in timeout={800}>
        <MotionPaper
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          elevation={0}
          sx={{
            mb: 6,
            p: { xs: 4, md: 6 },
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.secondary.main, 0.8)})`,
            position: 'relative',
            overflow: 'hidden',
            color: 'white',
          }}
        >
          {/* Decorative circles */}
          <Box
            sx={{
              position: 'absolute',
              top: -100,
              right: -100,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: alpha(theme.palette.common.white, 0.1),
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -50,
              left: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: alpha(theme.palette.common.white, 0.05),
            }}
          />

          <Grid container spacing={4} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid item xs={12} md="auto">
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                  src={imagePreview || profileData.photoUrl || "/assets/expert-placeholder.jpg"}
                  alt={`${profileData.firstName} ${profileData.lastName}`}
                  sx={{
                    width: { xs: 140, md: 180 },
                    height: { xs: 140, md: 180 },
                    border: `6px solid ${alpha(theme.palette.common.white, 0.3)}`,
                    boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                />

                {isEditing && (
                  <IconButton
                    color="primary"
                    aria-label="upload picture"
                    component="label"
                    sx={{
                      position: 'absolute',
                      bottom: 5,
                      right: 5,
                      bgcolor: theme.palette.common.white,
                      boxShadow: theme.shadows[4],
                      '&:hover': {
                        bgcolor: theme.palette.grey[100],
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={handleImageChange}
                    />
                    <PhotoCameraIcon />
                  </IconButton>
                )}

                {profileData.status === 'approved' && (
                  <VerifiedIcon
                    sx={{
                      position: 'absolute',
                      top: 5,
                      right: 5,
                      fontSize: 36,
                      color: theme.palette.success.main,
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                    }}
                  />
                )}
              </Box>
            </Grid>

            {/* Company Logo Upload */}
            <Grid item xs={12} md="auto">
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Typography variant="caption" sx={{ color: 'white', mb: 1, display: 'block', textAlign: 'center' }}>
                  Company Logo
                </Typography>
                <Avatar
                  src={logoPreview || profileData.companyLogoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(profileData.organizationName || 'Company')}&background=random&size=120&bold=true`}
                  alt={profileData.organizationName || 'Company'}
                  variant="rounded"
                  sx={{
                    width: { xs: 100, md: 120 },
                    height: { xs: 100, md: 120 },
                    border: `4px solid ${alpha(theme.palette.common.white, 0.3)}`,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                />

                {isEditing && (
                  <IconButton
                    color="primary"
                    aria-label="upload logo"
                    component="label"
                    sx={{
                      position: 'absolute',
                      bottom: 5,
                      right: 5,
                      bgcolor: theme.palette.common.white,
                      boxShadow: theme.shadows[4],
                      '&:hover': {
                        bgcolor: theme.palette.grey[100],
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={handleLogoChange}
                    />
                    <BusinessIcon />
                  </IconButton>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={profileData.userType === 'expert' ? 'Expert' : profileData.userType === 'admin' ? 'Admin' : 'Member'}
                  sx={{
                    bgcolor: profileData.userType === 'expert' ? theme.palette.secondary.main : alpha(theme.palette.common.white, 0.3),
                    color: 'white',
                    fontWeight: 600,
                    px: 2,
                    height: 32,
                    fontSize: '0.95rem'
                  }}
                />

                <Chip
                  icon={<LocationOnIcon sx={{ color: 'white !important' }} />}
                  label={profileData.country || 'Location not set'}
                  sx={{
                    bgcolor: alpha(theme.palette.common.white, 0.2),
                    color: 'white',
                    height: 32
                  }}
                />

                <Chip
                  label={profileData.status}
                  color={profileData.status === 'approved' ? 'success' : profileData.status === 'pending' ? 'warning' : 'error'}
                  sx={{ height: 32 }}
                />
              </Box>

              {isEditing ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="First Name"
                      name="firstName"
                      value={profileData.firstName}
                      onChange={handleInputChange}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                      sx={{
                        '& .MuiInputBase-root': {
                          bgcolor: 'transparent',
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Last Name"
                      name="lastName"
                      value={profileData.lastName}
                      onChange={handleInputChange}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                      sx={{
                        '& .MuiInputBase-root': {
                          bgcolor: 'transparent',
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Title/Position"
                      name="title"
                      value={profileData.title || ''}
                      onChange={handleInputChange}
                      error={!!errors.title}
                      helperText={errors.title}
                      sx={{
                        '& .MuiInputBase-root': {
                          bgcolor: 'transparent',
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Organization"
                      name="organizationName"
                      value={profileData.organizationName || ''}
                      onChange={handleInputChange}
                      error={!!errors.organizationName}
                      helperText={errors.organizationName}
                      sx={{
                        '& .MuiInputBase-root': {
                          bgcolor: 'transparent',
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Country"
                      name="country"
                      value={profileData.country || ''}
                      onChange={handleInputChange}
                      error={!!errors.country}
                      helperText={errors.country}
                      sx={{
                        '& .MuiInputBase-root': {
                          bgcolor: 'transparent',
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    {profileData.firstName} {profileData.lastName}
                  </Typography>

                  <Typography variant="h6" sx={{ opacity: 0.95, mb: 1, fontWeight: 500 }}>
                    {profileData.title}
                  </Typography>

                  <Typography variant="subtitle1" sx={{ opacity: 0.85, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon sx={{ fontSize: 20 }} />
                    {profileData.organizationName}
                  </Typography>
                </>
              )}

              {!isEditing && (
                <Box sx={{ display: 'flex', mt: 3, gap: 2 }}>
                  {profileData.userType === 'expert' && (
                    <Button
                      variant="contained"
                      startIcon={<ArticleIcon />}
                      onClick={() => navigate('/expert/articles')}
                      sx={{
                        bgcolor: alpha(theme.palette.secondary.main, 0.9),
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                          bgcolor: theme.palette.secondary.main,
                          transform: 'translateY(-2px)'
                        },
                        boxShadow: theme.shadows[4],
                        transition: 'all 0.2s'
                      }}
                    >
                      My Content
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEditToggle}
                    sx={{
                      bgcolor: alpha(theme.palette.common.white, 0.25),
                      color: 'white',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.common.white, 0.35),
                        transform: 'translateY(-2px)'
                      },
                      boxShadow: theme.shadows[4],
                      transition: 'all 0.2s'
                    }}
                  >
                    Edit Profile
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>

          {isEditing && (
            <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleEditToggle}
                disabled={saving}
                sx={{
                  borderColor: alpha(theme.palette.common.white, 0.5),
                  color: 'white',
                  '&:hover': {
                    borderColor: theme.palette.common.white,
                    bgcolor: alpha(theme.palette.common.white, 0.1)
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSubmit}
                disabled={saving}
                sx={{
                  bgcolor: theme.palette.common.white,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.9),
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          )}
        </MotionPaper>
      </Fade>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Professional Summary */}
          <Grow in timeout={1000}>
            <Box>
              <SectionHeader>
                <Typography variant="h5" fontWeight={600}>
                  Professional Summary
                </Typography>
              </SectionHeader>

              <InfoCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <CardContent sx={{ p: 3 }}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={5}
                      label="Professional Experience"
                      name="experience"
                      value={profileData.experience || ''}
                      onChange={handleInputChange}
                      placeholder="Describe your professional background, expertise, and achievements..."
                    />
                  ) : (
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                      {profileData.experience || 'No professional summary provided'}
                    </Typography>
                  )}
                </CardContent>
              </InfoCard>
            </Box>
          </Grow>

          {/* Organization Details */}
          <Grow in timeout={1200}>
            <Box sx={{ mt: 4 }}>
              <SectionHeader>
                <Typography variant="h5" fontWeight={600}>
                  Organization
                </Typography>
              </SectionHeader>

              <InfoCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <CardContent sx={{ p: 3 }}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Organization Brief"
                      name="organizationBrief"
                      value={profileData.organizationBrief || ''}
                      onChange={handleInputChange}
                      placeholder="Tell us about your organization..."
                    />
                  ) : (
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                      {profileData.organizationBrief || 'No organization details provided'}
                    </Typography>
                  )}
                </CardContent>
              </InfoCard>
            </Box>
          </Grow>

          {/* Areas of Expertise */}
          <Grow in timeout={1400}>
            <Box sx={{ mt: 4 }}>
              <SectionHeader>
                <Typography variant="h5" fontWeight={600}>
                  Areas of Expertise
                </Typography>
              </SectionHeader>

              <InfoCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <CardContent sx={{ p: 3 }}>
                  {isEditing ? (
                    <FormControl fullWidth error={!!errors.selectedSectors}>
                      <InputLabel>Sectors</InputLabel>
                      <Select
                        multiple
                        value={profileData.selectedSectors || []}
                        onChange={handleSectorChange}
                        input={<OutlinedInput label="Sectors" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip
                                key={value}
                                label={value}
                                size="small"
                                sx={{
                                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                                  color: theme.palette.primary.main,
                                  fontWeight: 500
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {availableSectors.map((sector) => (
                          <MenuItem key={sector} value={sector}>
                            {sector}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.selectedSectors && <FormHelperText>{errors.selectedSectors}</FormHelperText>}
                    </FormControl>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {profileData.selectedSectors && profileData.selectedSectors.length > 0 ? (
                        profileData.selectedSectors.map((sector, idx) => (
                          <Chip
                            key={idx}
                            label={sector}
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontWeight: 500,
                              borderRadius: 2,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                              }
                            }}
                          />
                        ))
                      ) : (
                        <Typography color="text.secondary">No sectors selected</Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </InfoCard>
            </Box>
          </Grow>

          {/* Expert-Specific Fields */}
          {profileData.userType === 'expert' && (
            <>
              {/* Expert Tag */}
              <Grow in timeout={1600}>
                <Box sx={{ mt: 4 }}>
                  <SectionHeader>
                    <Typography variant="h5" fontWeight={600}>
                      Expert Tag
                    </Typography>
                  </SectionHeader>

                  <InfoCard
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          label="Expert Tag"
                          name="expertTag"
                          value={profileData.expertProfile?.tag || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., Industry Expert, Innovation Leader, etc."
                          helperText="A short tag that describes your expertise"
                        />
                      ) : (
                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                          {profileData.expertProfile?.tag || 'No expert tag provided'}
                        </Typography>
                      )}
                    </CardContent>
                  </InfoCard>
                </Box>
              </Grow>

              {/* Services Offered */}
              <Grow in timeout={1800}>
                <Box sx={{ mt: 4 }}>
                  <SectionHeader>
                    <Typography variant="h5" fontWeight={600}>
                      Services Offered
                    </Typography>
                  </SectionHeader>

                  <InfoCard
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="Services Offered"
                          name="expertOffers"
                          value={profileData.expertProfile?.offers || ''}
                          onChange={handleInputChange}
                          placeholder="Describe the services you offer (separate with commas if multiple)"
                          helperText="Enter services separated by commas (e.g., Consulting, Training, Speaking)"
                        />
                      ) : (
                        <Box>
                          {profileData.expertProfile?.offers ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {profileData.expertProfile.offers.split(', ').map((offer: string, idx: number) => (
                                <Chip
                                  key={idx}
                                  label={offer}
                                  sx={{
                                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                    color: theme.palette.secondary.main,
                                    fontWeight: 500,
                                    borderRadius: 2
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body1" color="text.secondary">
                              No services listed
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </InfoCard>
                </Box>
              </Grow>
            </>
          )}
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {/* Account Information */}
          <Grow in timeout={1000}>
            <Box>
              <SectionHeader>
                <Typography variant="h5" fontWeight={600}>
                  Account Info
                </Typography>
              </SectionHeader>

              <InfoCard
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="body2" color="text.secondary">
                        Account Type
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={600}>
                      {profileData.userType === 'expert' ? 'Expert' : profileData.userType === 'admin' ? 'Admin' : 'Member'}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <VerifiedIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                    </Box>
                    <Chip
                      label={profileData.status}
                      size="small"
                      color={profileData.status === 'approved' ? 'success' : profileData.status === 'pending' ? 'warning' : 'error'}
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  {isEditing && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <FormControl fullWidth size="small">
                        <InputLabel>Segment</InputLabel>
                        <Select
                          name="segment"
                          value={profileData.segment || ''}
                          onChange={(e) => handleInputChange({ target: { name: 'segment', value: e.target.value } } as any)}
                          error={!!errors.segment}
                          label="Segment"
                        >
                          {segments.map((segment) => (
                            <MenuItem key={segment} value={segment}>
                              {segment}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.segment && <FormHelperText error>{errors.segment}</FormHelperText>}
                      </FormControl>
                    </>
                  )}

                  {!isEditing && profileData.segment && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                          <Typography variant="body2" color="text.secondary">
                            Segment
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight={600}>
                          {profileData.segment}
                        </Typography>
                      </Box>
                    </>
                  )}

                  {profileData.email && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                          <Typography variant="body2" color="text.secondary">
                            Email
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                          {profileData.email}
                        </Typography>
                      </Box>
                    </>
                  )}
                </CardContent>
              </InfoCard>
            </Box>
          </Grow>

          {/* Quick Stats */}
          <Grow in timeout={1200}>
            <Box sx={{ mt: 3 }}>
              <InfoCard
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Profile Completion
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'inline-flex', my: 2 }}>
                    <CircularProgress
                      variant="determinate"
                      value={calculateProfileCompletion(profileData)}
                      size={100}
                      thickness={4}
                      sx={{ color: theme.palette.primary.main }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="h5" fontWeight={700} color="primary">
                        {calculateProfileCompletion(profileData)}%
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Keep your profile updated to increase visibility
                  </Typography>
                </CardContent>
              </InfoCard>
            </Box>
          </Grow>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

// Helper function to calculate profile completion percentage
function calculateProfileCompletion(profile: User | null): number {
  if (!profile) return 0;

  const fields = [
    profile.firstName,
    profile.lastName,
    profile.title,
    profile.organizationName,
    profile.organizationBrief,
    profile.segment,
    profile.country,
    (profile.selectedSectors?.length ?? 0) > 0,
    profile.experience,
    profile.photoUrl
  ];

  const completed = fields.filter(Boolean).length;
  return Math.round((completed / fields.length) * 100);
}

export default ProfilePage;
